import { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';

const API_BASE = '/api';

interface Service {
  id: string;
  name: string;
  namespace: string;
  version: string;
  instances: number;
  healthy_instances: number;
  status: string;
  protocol: string;
  port: number;
  cpu_usage_percent: number;
  memory_usage_percent: number;
  requests_per_second: number;
  avg_latency_ms: number;
  error_rate: number;
  circuit_breaker: string;
}

interface Statistics {
  total_services: number;
  healthy_services: number;
  degraded_services: number;
  total_instances: number;
  healthy_instances: number;
  instance_health_percent: number;
  active_policies: number;
  total_rps: number;
  avg_latency_ms: number;
  open_circuit_breakers: number;
}

export default function ServiceMeshPage() {
  const [services, setServices] = useState<Service[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/servicemesh/services`),
        fetch(`${API_BASE}/servicemesh/statistics`)
      ]);
      setServices(await servicesRes.json());
      setStatistics(await statsRes.json());
    } catch (error) {
      console.error('Error fetching service mesh data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800';
      case 'degraded': return 'bg-yellow-100 text-yellow-800';
      case 'unhealthy': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCircuitBreakerColor = (state: string) => {
    switch (state) {
      case 'closed': return 'bg-green-100 text-green-800';
      case 'half_open': return 'bg-yellow-100 text-yellow-800';
      case 'open': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Service Mesh</h1>
          <p className="mt-2 text-gray-600">Monitor microservices, traffic policies, and service-to-service communication</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Services</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_services}</p>
              <p className="text-sm text-green-600">{statistics.healthy_services} healthy</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Instance Health</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.instance_health_percent}%</p>
              <p className="text-sm text-gray-500">{statistics.healthy_instances}/{statistics.total_instances}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total RPS</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.total_rps.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Latency</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.avg_latency_ms}ms</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Open Circuit Breakers</p>
              <p className="text-2xl font-bold text-red-600">{statistics.open_circuit_breakers}</p>
            </div>
          </div>
        )}

        {/* Services Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Services ({services.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Namespace</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instances</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RPS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Latency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Circuit Breaker</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {services.map((service) => (
                  <tr key={service.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{service.name}</div>
                      <div className="text-sm text-gray-500">{service.version} | {service.protocol}:{service.port}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{service.namespace}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(service.status)}`}>
                        {service.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{service.healthy_instances}/{service.instances}</div>
                      <div className="text-xs text-gray-500">CPU: {service.cpu_usage_percent}% | Mem: {service.memory_usage_percent}%</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {service.requests_per_second.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{service.avg_latency_ms}ms</div>
                      <div className="text-xs text-gray-500">Error: {service.error_rate}%</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getCircuitBreakerColor(service.circuit_breaker)}`}>
                        {service.circuit_breaker}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
